import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type {
  AlignSelfProps,
  BorderColorProps,
  JustifyContentProps,
  OverflowProps,
  PositionProps,
  WidthProps,
  ZIndexProps,
} from 'styled-system';
import {
  alignItems,
  alignSelf,
  borderColor,
  color,
  flex,
  flexDirection,
  justifyContent,
  maxWidth,
  overflow,
  position,
  space,
  textAlign,
  width,
  zIndex,
} from 'styled-system';

import CountDown from 'common/components/CountDown';
import { Flex } from 'common/components/Flex';
import LegalDisclaimer from 'common/components/LegalDisclaimer';
import { HeadlineH5, SubtitleST3 } from 'common/components/Typography';
import { Code, Stoerer } from 'common/components/Voucher';
import CtaButtonWithDrawer from 'common/components/Voucher/CtaButtonWithDrawer';
import { LEGAL_DISCLAIMER_BANNER_Z_INDEX } from 'common/constants/z-index';
import type { Color } from 'common/theme/colors/types';
import type { ColorProps, SpaceProps } from 'common/theme/system-types';
import type { ThemeBottomProps, ThemeRightProps } from 'common/theme/system-utilities';
import { boxSizing, themeBottom, themeRight } from 'common/theme/system-utilities';
import type { BaseBannerProps } from 'common/types/Banner';
import TopBannerContentWrapper from './TopBannerContentWrapper';

export interface BannerProps extends BaseBannerProps {
  onPromoView: () => void;
  onPromoClick: () => void;
}

const TITLES_MAX_WIDTH = {
  maxWidth: ['100%', null, '429px', '569px'],
};

const ContentWrapper = styled(TopBannerContentWrapper)<any>`
  ${position};
  ${alignItems};
  ${flexDirection};
  ${position};
  ${space};
  ${boxSizing};
`;

const contentWrapperStyles: ComponentProps<typeof ContentWrapper> = {
  position: 'relative',
  alignItems: 'center',
  flexDirection: ['column', null, 'row'],
  pl: ['sp_8', 'sp_24', 'sp_48', 'sp_64'],
  pr: ['sp_8', 'sp_24'],
  py: ['sp_8', 'sp_12', 'sp_24'],
  boxSizing: 'border-box',
};

const StyledStoerer = styled(Stoerer)<any>`
  ${flex};
  ${textAlign};
  ${space};
  ${color};
`;

const stoererStyles: ComponentProps<typeof StyledStoerer> = {
  flex: 'none',
  textAlign: 'center',
  my: 'sp_4',
  mr: [null, null, 'sp_48'],
};

const UnstyledTitles = styled(Flex)<any>`
  ${maxWidth};
`;

const Titles = (props: ComponentProps<typeof UnstyledTitles>) => (
  <UnstyledTitles
    flexDirection={['column', null, 'row']}
    flexGrow={1}
    flexWrap="wrap"
    alignItems="center"
    justifyContent={['center', null, 'flex-start']}
    mr={[null, null, 'sp_48']}
    {...props}
  />
);

export const Title = styled(HeadlineH5)<ColorProps & WidthProps>`
  ${color};
  ${width};
`;

export const titleStyles: ComponentProps<typeof Title> & { 'data-testid': string } = {
  'data-testid': 'title',
  as: 'h2',
  mt: ['sp_8', 'sp_12', 'sp_0'],
  mb: ['sp_4', null, null, 'sp_8'],
  color: 'sys.primary.background.default',
  textAlign: ['center', null, 'left'],
  mr: [null, null, 'sp_24'],
  width: [1, null, 'auto'],
};

export const SubTitle = styled(SubtitleST3)<OverflowProps & ColorProps & WidthProps>`
  ${overflow};
  ${color};
  ${width};
`;

export const subTitleStyles: ComponentProps<typeof SubTitle> & { 'data-testid': string } = {
  'data-testid': 'subtitle',
  as: 'p',
  overflow: 'hidden',
  textAlign: ['center', null, 'left'],
  mb: ['sp_8', 'sp_12', 'sp_0'],
  mt: 'sp_0',
  width: [1, null, 'auto'],
};

const UnstyledFooter = styled(Flex)``;

const Footer = (props: ComponentProps<typeof UnstyledFooter>) => (
  <UnstyledFooter
    flexDirection={['column', 'row']}
    justifyContent={['center', 'space-around', 'flex-start']}
    alignItems="center"
    flexGrow={2}
    flexWrap="wrap"
    width={[1, null, 'auto']}
    {...props}
  />
);

export const StyledCountDown = styled(CountDown)<
  ColorProps & BorderColorProps & JustifyContentProps & SpaceProps
>`
  ${justifyContent};
  ${space};
  ${color};
  ${borderColor};

  > p,
  > div {
    margin-bottom: 0;
  }
`;

export const countDownStyles: Partial<ComponentProps<typeof StyledCountDown>> = {
  color: 'sys.neutral.border.strong',
  borderColor: 'sys.neutral.border.strong',
  justifyContent: 'center',
  mr: ['sp_0', null, 'sp_24', 'sp_48'],
  my: 'sp_8',
};

export const codeStyles: ComponentProps<typeof Code> = {
  py: 'sp_4',
  px: 'sp_8',
  my: 'sp_8',
};

const UnstyledStyledTopBannerCtaButton = styled(CtaButtonWithDrawer)<any>`
  ${space};
`;

const StyledTopBannerCtaButton = (
  props: ComponentProps<typeof UnstyledStyledTopBannerCtaButton>
) => <UnstyledStyledTopBannerCtaButton my={['sp_0', null, 'sp_8']} {...props} />;

export const StyledLegalDisclaimer = styled(LegalDisclaimer)<
  SpaceProps & PositionProps & ZIndexProps & ThemeBottomProps & ThemeRightProps & AlignSelfProps
>`
  ${space};
  ${position};
  ${zIndex};
  ${themeBottom};
  ${themeRight};
  ${alignSelf};
`;

export const legalDisclaimerStyles: Omit<
  ComponentProps<typeof StyledLegalDisclaimer>,
  'label' | 'content' | 'color'
> = {
  position: 'absolute',
  zIndex: LEGAL_DISCLAIMER_BANNER_Z_INDEX,
  themeRight: 'sp_0',
  themeBottom: ['sp_0', null, 'sp_4'],
  p: 'sp_8',
};

// eslint-disable-next-line max-lines-per-function, complexity
const TopTextBanner = (props: BannerProps) => {
  const {
    bannerStartBgColor,
    bannerEndBgColor,
    legalDisclaimerContent,
    legalDisclaimerLinkLabel,
    legalDisclaimerLabelColor,
    countdownEndDate,
    countdownLabel,
    countdownColor,
    titleColor,
    subTitleColor,
    title,
    subTitle,
    voucherCode,
    voucherCodeLabel,
    voucherCodeTextColor,
    voucherCodeBoxBgColor,
    storer,
    storerTextColor,
    storerLineColor,
    ctaLabel,
    ctaColor,
    ctaDisclaimerText,
    onPromoView,
    onPromoClick,
  } = props;

  const hasFooter = countdownEndDate || voucherCode || ctaLabel;
  const hasTitles = title || subTitle;
  const hasDisclaimer = legalDisclaimerContent && legalDisclaimerLinkLabel;
  const hasCtaButton = !voucherCode && ctaLabel && ctaDisclaimerText;
  const hasStorer = !!storer;

  const hasContent = hasFooter || hasTitles || hasCtaButton || hasStorer;
  if (!hasContent) {
    return null;
  }

  return (
    <ContentWrapper
      {...contentWrapperStyles}
      startColor={bannerStartBgColor}
      endColor={bannerEndBgColor}
      onView={onPromoView}
    >
      {hasDisclaimer && (
        <StyledLegalDisclaimer
          {...legalDisclaimerStyles}
          color={legalDisclaimerLabelColor || 'text'}
          content={legalDisclaimerContent}
          label={legalDisclaimerLinkLabel}
        />
      )}
      <StyledStoerer
        {...stoererStyles}
        text={storer}
        borderColor={storerLineColor}
        color={storerTextColor}
      />
      {hasTitles && (
        <Titles {...(hasFooter ? TITLES_MAX_WIDTH : {})}>
          {title && (
            <Title {...titleStyles} color={titleColor as Color}>
              {title}
            </Title>
          )}
          {subTitle && (
            <SubTitle {...subTitleStyles} color={subTitleColor as Color}>
              {subTitle}
            </SubTitle>
          )}
        </Titles>
      )}
      {hasFooter && (
        <Footer>
          {countdownEndDate && (
            <StyledCountDown
              {...countDownStyles}
              endDate={countdownEndDate}
              label={countdownLabel}
              color={countdownColor as Color}
            />
          )}
          <Code
            {...codeStyles}
            code={voucherCode}
            label={voucherCodeLabel}
            bgColor={voucherCodeBoxBgColor}
            textColor={voucherCodeTextColor}
            onClick={onPromoClick}
          />
          {hasCtaButton && (
            <StyledTopBannerCtaButton
              ctaLabel={ctaLabel}
              ctaColor={ctaColor}
              ctaDisclaimerText={ctaDisclaimerText}
            />
          )}
        </Footer>
      )}
    </ContentWrapper>
  );
};

export default TopTextBanner;
